/**
 * Tweaks — the demo-driver panel.
 *
 * A presenter needs to change who is looking at the screen mid-conversation
 * without signing out and back in. That is the persona switch: it replaces the
 * identity the whole shell reads, so the rail, the top bar and the account
 * dialog all move together.
 *
 * Persisted to localStorage on purpose, and it is the ONLY thing in the app
 * that is. The rule the session store follows — nothing persists, a reload
 * restores the seed — exists so a demo is reproducible. A tweak is not demo
 * data; losing your persona and density on every reload is friction with no
 * upside. Application state stays in `sessionStore`, which still persists
 * nothing.
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';

/**
 * Who is driving. `role` and `initials` feed the shell's identity directly, so
 * switching persona changes the avatar, the rail greeting and the account
 * dialog in one move.
 */
export interface Persona {
  readonly id: string;
  readonly displayName: string;
  readonly initials: string;
  readonly role: string;
  /** Shown under the selector, so the presenter can narrate the switch. */
  readonly description: string;
}

/**
 * The default persona is declared separately from the list so the fallback
 * below needs no non-null assertion: a `readonly Persona[]` index is
 * `Persona | undefined` to the type system even when the array is a literal,
 * and asserting that away would hide a genuinely empty list.
 */
const BRAND_MANAGER: Persona = {
  id: 'brand-manager',
  displayName: 'Marcus Reinhardt',
  initials: 'MR',
  role: 'Brand Manager',
  description:
    'Brand-side manager. Tracks contracts and runs activations across their partner portfolio.',
};

export const PERSONAS: readonly Persona[] = [
  BRAND_MANAGER,
  {
    id: 'rights-holder',
    displayName: 'Sofia Almeida',
    initials: 'SA',
    role: 'Rights Holder — Commercial',
    description:
      'Sells and renews inventory. Cares about valuation, the price book and what a partner will pay.',
  },
  {
    id: 'agency-consultant',
    displayName: 'Daniel Owusu',
    initials: 'DO',
    role: 'Agency Consultant',
    description:
      'Advises both sides. Lives in Strategic Relevance and the portfolio decision matrix.',
  },
  {
    id: 'data-engineer',
    displayName: 'Priya Raman',
    initials: 'PR',
    role: 'Data Engineer',
    description:
      'Owns the pipelines behind the agents. Works in Data QA and the DCS scorecards, not the client decks.',
  },
  {
    id: 'administrator',
    displayName: 'Elena Costa',
    initials: 'EC',
    role: 'Administrator',
    description:
      'Configures the scoring model and publishes it. The only persona that can change SR weights.',
  },
];

export const DENSITIES = ['comfortable', 'dense'] as const;
export type Density = (typeof DENSITIES)[number];

interface TweaksState {
  readonly personaId: string;
  readonly density: Density;
  readonly pinSidebar: boolean;
  readonly collapseProfileRail: boolean;
  readonly showScopeFilters: boolean;
  /**
   * Reach the seeded worked MMM output at `/agent/mmm/output`.
   *
   * OFF by default, and that is the decision rather than a preference (DEC-49).
   * That page renders an illustrative worked example while the agent's own
   * dashboard renders a real delivery, and two screens of numbers one click
   * apart — one invented, one not — is how an illustrative figure ends up in a
   * client conversation. It stays reachable for demos, behind a switch a
   * presenter turns on deliberately.
   */
  readonly showWorkedOutputs: boolean;

  setPersona: (id: string) => void;
  setDensity: (density: Density) => void;
  toggle: (
    key: 'pinSidebar' | 'collapseProfileRail' | 'showScopeFilters' | 'showWorkedOutputs',
  ) => void;
  reset: () => void;
}

const DEFAULTS = {
  personaId: 'brand-manager',
  density: 'comfortable' as Density,
  pinSidebar: false,
  collapseProfileRail: false,
  showScopeFilters: true,
  showWorkedOutputs: false,
};

export const useTweaksStore = create<TweaksState>()(
  persist(
    (set) => ({
      ...DEFAULTS,
      setPersona: (personaId) => {
        set({ personaId });
      },
      setDensity: (density) => {
        set({ density });
      },
      toggle: (key) => {
        set((s) => ({ [key]: !s[key] }));
      },
      reset: () => {
        set(DEFAULTS);
      },
    }),
    {
      name: 'pnet-arena-tweaks',
      // Only the preferences persist. Actions are rebuilt on load, and storing
      // them would serialise functions into localStorage.
      partialize: (s) => ({
        personaId: s.personaId,
        density: s.density,
        pinSidebar: s.pinSidebar,
        collapseProfileRail: s.collapseProfileRail,
        showScopeFilters: s.showScopeFilters,
        showWorkedOutputs: s.showWorkedOutputs,
      }),
    },
  ),
);

/**
 * The active persona.
 *
 * Falls back to the first persona rather than returning undefined: a stored id
 * from an older build must not leave the shell with no identity at all.
 */
export const useActivePersona = (): Persona => {
  const personaId = useTweaksStore((s) => s.personaId);
  return PERSONAS.find((p) => p.id === personaId) ?? BRAND_MANAGER;
};
